import type { RobotProfileCatalogEntry, RobotProfileId } from "lib/robotProfileApi";

import go2Image from "assets/images/robots/go2.png";
import go2wImage from "assets/images/robots/go2w.png";
import gbwImage from "assets/images/robots/gbw.png";
import gbImage from "assets/images/robots/gb.png";
import lynxImage from "assets/images/robots/lynx.png";
import tron1Image from "assets/images/robots/tron1.png";
import spotImage from "assets/images/robots/spot.png";

export const PROFILE_ORDER: RobotProfileId[] = [
    "go2",
    "go2w",
    "gb",
    "gbw",
    "lynx",
    "tron1",
    "spot",
];

/** Product photos for profile cards. Missing entries use a placeholder. */
export const ROBOT_PROFILE_IMAGES: Partial<Record<RobotProfileId, string>> = {
    go2: go2Image,
    go2w: go2wImage,
    gb: gbImage,
    gbw: gbwImage,
    lynx: lynxImage,
    tron1: tron1Image,
    spot: spotImage,
};

/** Offline fallbacks matching each robot_control package launch/config defaults. */
export const FALLBACK_CATALOG: Record<RobotProfileId, RobotProfileCatalogEntry> = {
    go2: {
        methods: ["local_ap", "local_sta", "remote"],
        default_method: "local_sta",
        default_params: { robot_ip: "192.168.8.181", aes_128_key: "" },
        method_params: {
            local_ap: { robot_ip: "192.168.12.1", aes_128_key: "" },
            local_sta: { robot_ip: "192.168.8.181", aes_128_key: "" },
            remote: {
                serial_number: "",
                username: "",
                password: "",
                aes_128_key: "",
            },
        },
    },
    go2w: {
        methods: ["ethernet", "local_ap", "local_sta", "remote"],
        default_method: "ethernet",
        default_params: { robot_ip: "192.168.123.18", aes_128_key: "" },
        method_params: {
            ethernet: { robot_ip: "192.168.123.18", aes_128_key: "" },
            local_ap: { robot_ip: "192.168.12.1", aes_128_key: "" },
            local_sta: { robot_ip: "192.168.8.181", aes_128_key: "" },
            remote: {
                serial_number: "",
                username: "",
                password: "",
                aes_128_key: "",
            },
        },
    },
    gb: {
        methods: ["ethernet", "wifi"],
        default_method: "ethernet",
        default_params: {
            local_ip: "192.168.168.100",
            local_port: "43989",
            dog_ip: "192.168.168.169",
        },
        method_params: {
            ethernet: {
                local_ip: "192.168.168.100",
                local_port: "43989",
                dog_ip: "192.168.168.169",
            },
            wifi: {
                local_ip: "192.168.234.15",
                local_port: "43989",
                dog_ip: "192.168.234.1",
            },
        },
    },
    gbw: {
        methods: ["ethernet", "wifi"],
        default_method: "ethernet",
        default_params: {
            local_ip: "192.168.168.100",
            local_port: "43988",
            dog_ip: "192.168.168.168",
        },
        method_params: {
            ethernet: {
                local_ip: "192.168.168.100",
                local_port: "43988",
                dog_ip: "192.168.168.168",
            },
            wifi: {
                local_ip: "192.168.234.15",
                local_port: "43988",
                dog_ip: "192.168.234.1",
            },
        },
    },
    lynx: {
        methods: ["udp"],
        default_method: "udp",
        default_params: {
            local_ip: "10.21.31.10",
            local_port: "30000",
            robot_ip: "10.21.31.103",
            robot_port: "30000",
        },
        method_params: {},
    },
    tron1: {
        methods: ["sdk"],
        default_method: "sdk",
        default_params: {
            local_ip: "10.192.1.1",
            robot_ip: "10.192.1.2",
            accid: "SF_TRON1A_093",
        },
        method_params: {},
    },
    spot: {
        methods: ["ethernet", "network"],
        default_method: "ethernet",
        default_params: {
            local_ip: "192.168.50.5",
            hostname: "192.168.50.3",
            username: "",
            password: "",
            avoidance_m: "0.1",
        },
        method_params: {
            ethernet: {
                local_ip: "192.168.50.5",
                hostname: "192.168.50.3",
                username: "",
                password: "",
                avoidance_m: "0.1",
            },
            network: {
                hostname: "192.168.50.3",
                username: "",
                password: "",
                avoidance_m: "0.1",
            },
        },
    },
};

export type RobotProfileDraft = {
    method: string;
    params: Record<string, string>;
};

export function stringifyParams(raw: Record<string, unknown> | undefined): Record<string, string> {
    const out: Record<string, string> = {};
    if (!raw) return out;
    for (const [key, value] of Object.entries(raw)) {
        out[key] = value == null ? "" : String(value);
    }
    return out;
}

export function resolveDefaults(
    catalog: Record<RobotProfileId, RobotProfileCatalogEntry>,
    profile: RobotProfileId,
    method?: string
): RobotProfileDraft {
    const entry = catalog[profile] || FALLBACK_CATALOG[profile];
    const nextMethod =
        method ||
        entry.default_method ||
        entry.methods?.[0] ||
        FALLBACK_CATALOG[profile].default_method ||
        "sdk";
    const methodParams =
        entry.method_params?.[nextMethod] || FALLBACK_CATALOG[profile].method_params?.[nextMethod];
    if (methodParams) {
        return {
            method: nextMethod,
            params: stringifyParams(methodParams),
        };
    }
    return {
        method: nextMethod,
        params: stringifyParams({
            ...(FALLBACK_CATALOG[profile].default_params || {}),
            ...(entry.default_params || {}),
        }),
    };
}

export function buildDefaultDrafts(
    catalog: Record<RobotProfileId, RobotProfileCatalogEntry>
): Record<RobotProfileId, RobotProfileDraft> {
    return PROFILE_ORDER.reduce(
        (acc, id) => {
            acc[id] = resolveDefaults(catalog, id);
            return acc;
        },
        {} as Record<RobotProfileId, RobotProfileDraft>
    );
}
